import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, MessageSquareHeart, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { submitFeedback } from "@/services/publicFeedbackService";

interface LaravelErrorResponse {
    message: string;
    errors?: Record<string, string[]>;
}

export default function FeedbackPage() {
    const parseError = (error: unknown): string => {
        if (axios.isAxiosError<LaravelErrorResponse>(error)) {
            const data = error.response?.data;
            if (data?.errors) {
                const firstKey = Object.keys(data.errors)[0];
                return data.errors[firstKey][0];
            }
            return data?.message || "Terjadi kesalahan. Silakan coba lagi.";
        }
        return "Terjadi kesalahan yang tidak diketahui.";
    };

    // Dark mode
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        } else {
            setIsDark(false);
            document.documentElement.classList.remove("dark");
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        if (newMode) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    };

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [institution, setInstitution] = useState("");
    const [message, setMessage] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);

    const isFormComplete = !!message.trim();

    const handleSubmit = async () => {
        if (!message.trim()) {
            setSubmitError("Masukan / feedback wajib diisi.");
            return;
        }

        setSubmitError(null);
        setIsSubmitting(true);
        try {
            await submitFeedback({
                name: name || undefined,
                email: email || undefined,
                institution: institution || undefined,
                message,
            });
            setIsSuccess(true);
        } catch (error) {
            setSubmitError(parseError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const ThemeToggle = () => (
        <div className="absolute top-4 right-4 z-10">
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="w-9 h-9 rounded-full bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
                {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
            </Button>
        </div>
    );

    if (isSuccess) {
        return (
            <div className="relative min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
                <ThemeToggle />
                <Card className="max-w-md w-full rounded-3xl shadow-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <CardContent className="pt-10 pb-10 text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                            <CheckCircle2 className="w-9 h-9 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Terima Kasih!</h2>
                        <p className="text-slate-600 dark:text-slate-400">
                            Masukan Anda sudah kami terima dan akan menjadi bahan evaluasi kami untuk kegiatan selanjutnya.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 py-10 px-4">
            <ThemeToggle />
            <div className="max-w-lg mx-auto space-y-6">
                <div className="text-center space-y-2">
                    <div className="mx-auto w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg shadow-indigo-600/20 p-2">
                        <img src="/konaspi.png" alt="Logo KONASPI" className="w-full h-full object-contain" />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 dark:text-white">Form Feedback</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                        Bantu kami menjadi lebih baik dengan mengisi masukan singkat mengenai kegiatan ini.
                    </p>
                </div>

                <Card className="rounded-3xl shadow-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <CardHeader>
                        <CardTitle className="text-base text-slate-900 dark:text-white flex items-center gap-2">
                            <MessageSquareHeart className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                            Data Diri (Opsional)
                        </CardTitle>
                        <CardDescription className="dark:text-slate-400">
                            Semua isian di bawah ini bersifat opsional, boleh dikosongkan.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-1.5">
                            <Label className="dark:text-slate-300">Nama</Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Nama lengkap Anda"
                                className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="dark:text-slate-300">Alamat Email</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="nama@email.com"
                                className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="dark:text-slate-300">Institusi</Label>
                            <Input
                                value={institution}
                                onChange={(e) => setInstitution(e.target.value)}
                                placeholder="Nama institusi/instansi Anda"
                                className="h-11 rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-3xl shadow-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <CardHeader>
                        <CardTitle className="text-base text-slate-900 dark:text-white">Masukan Anda *</CardTitle>
                        <CardDescription className="dark:text-slate-400">
                            Ceritakan kesan, saran, atau kritik Anda mengenai kegiatan ini.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Tulis masukan Anda di sini..."
                            rows={5}
                            className="rounded-xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white resize-none"
                        />
                    </CardContent>
                </Card>

                {submitError && (
                    <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-3">{submitError}</div>
                )}

                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormComplete}
                    className="w-full h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-base font-bold shadow-lg shadow-indigo-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                    Kirim Feedback
                </Button>
            </div>
        </div>
    );
}
